import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import { reasonLabel } from './reasons';
import { format12hClock } from '../../../utils/datetime';

// Confirmation view for the in-flight edit set. Reasons are captured at
// anchor / remove time, so this modal renders them read-only. Confirm fires
// the appropriate createNextmvRun(s) via the parent.

const StyledDiv = styledComponent('div', {
  '& .urm-section': { marginBottom: theme.spacing.sectionGap },
  '& .urm-section-title': {
    fontSize: theme.typography.label.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
    marginBottom: theme.spacing.fieldGap,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  '& .urm-row': {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: theme.spacing.fieldGap,
    padding: '8px 0',
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    alignItems: 'center',
  },
  '& .urm-row:last-of-type': { borderBottom: 'none' },
  '& .urm-row-name': {
    fontSize: theme.typography.body.fontSize,
    color: theme.colors.textDark,
    fontWeight: theme.fontWeights.medium,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.gapMd,
  },
  '& .urm-row-meta': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    fontFamily: 'monospace',
  },
  '& .urm-badge': {
    fontSize: theme.typography.microLabel.fontSize,
    fontWeight: theme.fontWeights.semibold,
    padding: '2px 6px',
    borderRadius: theme.radii.input,
    background: theme.colors.cautionBg,
    color: theme.colors.warningText,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  '& .urm-badge.is-missed-stop': {
    background: theme.colors.successBg,
    color: theme.colors.successText,
  },
  '& .urm-empty': {
    color: theme.colors.textMuted,
    fontStyle: 'italic',
    fontSize: theme.typography.label.fontSize,
  },
});

function nameOf(stop) {
  return stop?.displayName || stop?.id || stop?.stop || 'Unnamed stop';
}

function UpdateRouteModal({
  show,
  routeName,
  stopByKey,
  anchoredStops,
  anchoredKeys,
  removeSelections,
  removeSelectionKeys,
  pendingMissedStops,
  onConfirm,
  onCancel,
}) {
  const hasNothingDirty =
    anchoredKeys.length === 0 &&
    removeSelectionKeys.length === 0 &&
    pendingMissedStops.length === 0;

  const handleConfirm = () => {
    if (hasNothingDirty) return;
    onConfirm();
  };

  return (
    <Modal show={show} onHide={onCancel} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Update {routeName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <StyledDiv>
          {hasNothingDirty && (
            <div className="urm-empty">No changes to confirm.</div>
          )}

          {pendingMissedStops.length > 0 && (
            <div className="urm-section" data-testid="urm-missed-stops">
              <div className="urm-section-title">
                Adding from yesterday&apos;s missed stops (
                {pendingMissedStops.length})
              </div>
              {pendingMissedStops.map((m) => (
                <div
                  key={m.rowKey || m.stopId}
                  className="urm-row"
                  data-testid={`urm-missed-stop-row-${m.rowKey || m.stopId}`}
                >
                  <div className="urm-row-name">
                    {m.stopId || 'Unnamed stop'}
                    <span className="urm-badge is-missed-stop">
                      Missed stop
                    </span>
                  </div>
                  <div className="urm-row-meta">
                    from {m.originalRoute || '—'}
                    {m.originalDate ? ` (${m.originalDate})` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}

          {anchoredKeys.length > 0 && (
            <div className="urm-section" data-testid="urm-anchored">
              <div className="urm-section-title">
                Pinned stops ({anchoredKeys.length})
              </div>
              {anchoredKeys.map((key) => {
                const stop = stopByKey.get(key);
                const anchor = anchoredStops.get(key);
                return (
                  <div
                    key={key}
                    className="urm-row"
                    data-testid={`urm-anchored-row-${key}`}
                  >
                    <div className="urm-row-name">
                      {nameOf(stop)}
                      <span className="urm-badge">Pinned</span>
                    </div>
                    <div className="urm-row-meta">
                      {format12hClock(anchor.startTimeWindow)}–
                      {format12hClock(anchor.endTimeWindow)} ·{' '}
                      {reasonLabel(anchor.reason?.value)}
                      {anchor.reason?.note ? ` (${anchor.reason.note})` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {removeSelectionKeys.length > 0 && (
            <div className="urm-section" data-testid="urm-removed">
              <div className="urm-section-title">
                Removing ({removeSelectionKeys.length})
              </div>
              {removeSelectionKeys.map((key) => {
                const stop = stopByKey.get(key);
                const entry = removeSelections.get(key);
                return (
                  <div
                    key={key}
                    className="urm-row"
                    data-testid={`urm-removed-row-${key}`}
                  >
                    <div className="urm-row-name">{nameOf(stop)}</div>
                    <div className="urm-row-meta">
                      {reasonLabel(entry?.reason?.value)}
                      {entry?.reason?.note ? ` (${entry.reason.note})` : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </StyledDiv>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancel} data-testid="urm-cancel">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={hasNothingDirty}
          className="btn-success"
          data-testid="urm-confirm"
        >
          Confirm Update Route
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

UpdateRouteModal.propTypes = {
  show: PropTypes.bool.isRequired,
  routeName: PropTypes.string.isRequired,
  stopByKey: PropTypes.instanceOf(Map).isRequired,
  anchoredStops: PropTypes.instanceOf(Map).isRequired,
  anchoredKeys: PropTypes.arrayOf(PropTypes.string),
  removeSelections: PropTypes.instanceOf(Map).isRequired,
  removeSelectionKeys: PropTypes.arrayOf(PropTypes.string),
  pendingMissedStops: PropTypes.arrayOf(
    PropTypes.shape({
      rowKey: PropTypes.string,
      stopId: PropTypes.string,
      originalRoute: PropTypes.string,
      originalDate: PropTypes.string,
    }),
  ),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

UpdateRouteModal.defaultProps = {
  anchoredKeys: [],
  removeSelectionKeys: [],
  pendingMissedStops: [],
};

export default UpdateRouteModal;
